import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import { getCachedGwApiAccessToken, getValidGwApiAccessToken, refreshGwApiAccessToken } from '@/auth/GatewayApiAccessToken';
import { isIOSWebView } from '@/utils';

const isDev = import.meta.env.MODE === 'development';
const forceGatewayTokenRefresh = import.meta.env.TOURPWA_API_GATEWAY_FORCE_TOKEN_REFRESH === 'true';
let gatewayAuthInstance: IPublicClientApplication | null = null;
let gatewayAuthAccount: AccountInfo | undefined;

const gatewayHttpClient = axios.create({
  baseURL: isDev ? '/gateway' : import.meta.env.TOURPWA_API_GATEWAY_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getGatewayAuthAccount = () => {
  if (gatewayAuthAccount) {
    return gatewayAuthAccount;
  }

  if (!gatewayAuthInstance) {
    return undefined;
  }

  const account = gatewayAuthInstance.getActiveAccount() || gatewayAuthInstance.getAllAccounts()[0];
  if (account) {
    gatewayAuthInstance.setActiveAccount(account);
    gatewayAuthAccount = account;
  }

  return gatewayAuthAccount;
};

export const setupGatewayApiAuth = (instance: IPublicClientApplication, account?: AccountInfo) => {
  gatewayAuthInstance = instance;
  gatewayAuthAccount = account || instance.getActiveAccount() || instance.getAllAccounts()[0] || undefined;

  if (gatewayAuthAccount) {
    instance.setActiveAccount(gatewayAuthAccount);
  }
};

const getGatewayToken = async (forceRefresh = false) => {
  if (isIOSWebView) {
    // In iOS request the token from the native app
    return forceRefresh ? refreshGwApiAccessToken() : getValidGwApiAccessToken();
  }

  if (gatewayAuthInstance) {
    const account = getGatewayAuthAccount();
    return forceRefresh ? refreshGwApiAccessToken(gatewayAuthInstance, account) : getValidGwApiAccessToken(gatewayAuthInstance, account);
  }

  if (forceRefresh) {
    throw new Error('API Gateway token refresh is unavailable. Please sign in again.');
  }

  const cachedToken = getCachedGwApiAccessToken();
  if (!cachedToken) {
    throw new Error('No valid API Gateway token found. Please sign in again.');
  }

  return cachedToken;
};

// eslint-disable-next-line no-unused-vars
type GatewayAuthRequest<T> = (token: string) => Promise<AxiosResponse<T>>;

const withGatewayAuth = async <T>(request: GatewayAuthRequest<T>, forceRefresh = false): Promise<AxiosResponse<T>> => {
  const token = await getGatewayToken(forceRefresh);

  try {
    return await request(token);
  } catch (error) {
    if (!forceRefresh && axios.isAxiosError(error) && error.response?.status === 401) {
      return withGatewayAuth<T>(request, true);
    }

    throw error;
  }
};

const postWithGatewayAuth = async <T>(url: string, data = {}, config: AxiosRequestConfig = {}, forceRefresh = false): Promise<AxiosResponse<T>> => {
  return withGatewayAuth<T>(
    (token) =>
      gatewayHttpClient.post<T>(url, data, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      }),
    forceRefresh,
  );
};

const getWithGatewayAuth = async <T>(url: string, config: AxiosRequestConfig = {}, forceRefresh = false): Promise<AxiosResponse<T>> => {
  return withGatewayAuth<T>(
    (token) =>
      gatewayHttpClient.get<T>(url, {
        ...config,
        headers: {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        },
      }),
    forceRefresh,
  );
};

export const gatewayPostRequest = async <T>(url: string, data = {}, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
  return postWithGatewayAuth<T>(url, data, config, forceGatewayTokenRefresh);
};

export const gatewayGetRequest = async <T>(url: string, config: AxiosRequestConfig = {}): Promise<AxiosResponse<T>> => {
  return getWithGatewayAuth<T>(url, config, forceGatewayTokenRefresh);
};
